import { msg } from '@lingui/core/macro';

import { defineEnum } from '@/shared/patterns/define-enum';

// Rótulos, tons e ícones dos valores técnicos da cessão (U19, U33). Leve de propósito: entra no
// `validateSearch` da lista (filtro Status), que vai para o JS inicial.

/**
 * `BatchAssignmentStatus` da CreditAPI, como texto em `status` e a `[Description]` em `statusDisplay`.
 * O rótulo é o de `batchAssignmentSelectFilterOptions` (`creditNoteOptions.ts:219-227`); `Error` e
 * `Calculated` existem no backend e não estão no filtro do legado (ficam fora das opções). Fora do mapa,
 * a tela mostra o `statusDisplay`.
 */
export const BATCH_ASSIGNMENT_STATUS = defineEnum({
  Finished: { label: msg`Concluída`, tone: 'ok', icon: 'check-check' },
  Cancelled: { label: msg`Cancelada`, tone: 'neutral', icon: 'ban' },
  Liquidation: { label: msg`Liquidação`, tone: 'ok', icon: 'circle-check' },
  Timeout: { label: msg`Expirada`, tone: 'neutral', icon: 'clock' },
  Signatures: { label: msg`Coleta de assinaturas`, tone: 'warn', icon: 'pen-line' },
  Processing: { label: msg`Em processamento`, tone: 'info', icon: 'hourglass' },
  Open: { label: msg`Em Aberto`, tone: 'info', icon: 'circle-dashed' },
  Error: { label: msg`Erro`, tone: 'err', icon: 'triangle-alert', hidden: true },
  Calculated: { label: msg`Calculada`, tone: 'info', icon: 'file-check', hidden: true },
});

/**
 * "Alterar cálculo de cessão" (`CalculateOptionEnum`, RecalculateFormSchema.ts:3-8). Os rótulos mudam
 * entre o "Recalcular" do detalhe e o "Gerar cessão" da Nova cessão; cada tela usa o seu.
 */
export const CALCULATE_OPTION = { none: '0', assignmentApr: '1', assignmentCalculation: '2', assignmentAndApr: '3' } as const;
export type CalculateOption = (typeof CALCULATE_OPTION)[keyof typeof CALCULATE_OPTION];

/** Rótulos do "Recalcular" (`RecalculateForm.tsx:45-59`). */
export const RECALCULATE_OPTIONS = defineEnum(
  {
    '0': { label: msg`Calcular pelo ágio` },
    '1': { label: msg`Calcular pela taxa de cessão` },
    '2': { label: msg`Atualizar e calcular pelo ágio` },
    '3': { label: msg`Calcular por ágio OU taxa de cessão mínima` },
  },
  { numeric: true, order: ['0', '1', '2', '3'] },
);

/** Rótulos do "Gerar cessão" (`CreateBatchAssignment.tsx:37-53`). */
export const CREATE_CALCULATE_OPTIONS = defineEnum(
  {
    '0': { label: msg`Manter definições atuais` },
    '1': { label: msg`Calcular por taxa de cessão` },
    '2': { label: msg`Atualizar Ágio/Deságio das operações` },
    '3': { label: msg`Calcular por ágio OU taxa de cessão mínima` },
  },
  { numeric: true, order: ['0', '1', '2', '3'] },
);

/**
 * Modelo de cálculo (`assignmentCalculationModeOptions`, services/creditNote/options/index.ts:1-6), sem o
 * espaço no fim de "Valor Inicial " (U20-5). O valor vai como texto no corpo (o Cessionário usa o número).
 */
export const CALCULATION_MODE = defineEnum({
  Default: { label: msg`Padrão` },
  InitialValue: { label: msg`Valor Inicial` },
  PresentValueCreditNote: { label: msg`Valor Presente` },
  PresentValuePayment: { label: msg`Valor Presente Parcela` },
});

/** Tipo de taxa (`RecalculateForm.tsx:76-83`): muda o custo entre moeda (centavos) e percentual (fração). */
export const RATE_TYPE = defineEnum({
  Absolute: { label: msg`Valor absoluto` },
  Percentage: { label: msg`Percentual` },
});

/** Critério de cálculo (`assignmentAprCriteriaOptions`, services/creditNote/options/index.ts:8-16). */
export const APR_CRITERIA = defineEnum({
  V252DiasUteis: { label: msg`Base 252 - Dias úteis` },
  V252MesesX21: { label: msg`Base 252 - Meses x 21` },
  V360DiasCorridos: { label: msg`Base 360 - Dias corridos` },
  V360Meses: { label: msg`Base 360 - Meses` },
  V365DiasCorridos: { label: msg`Base 365 - Dias corridos` },
  V365Meses: { label: msg`Base 365 - Meses` },
  Irregular: { label: msg`Irregular` },
});

/** "Tipo de cálculo" da Nova cessão (`ammTypeOptions`, InformationTabCreditProduct.tsx:79-87), em ordem de rótulo. */
export const AMORTIZATION_TYPE = defineEnum(
  {
    Price: { label: msg`PRICE (B3)` },
    SAC: { label: msg`SAC (B3)` },
    PriceCoefficient: { label: msg`PRICE Coeficiente` },
    FGTS: { label: msg`FGTS` },
    StudentLoan: { label: msg`Financiamento estudantil` },
    Discount: { label: msg`Desconto` },
    ReceivablesAdvance: { label: msg`Antecipação de Recebíveis` },
  },
  { order: 'label' },
);

/** A categoria que mostra "Status do roteiro de liquidação: Pendente" (`IncludeBatchAssignment.tsx:221`). */
// eslint-disable-next-line lingui/no-unlocalized-strings -- valor técnico do fio (`productCategoryName`)
export const DEBT_PURCHASE_CATEGORY = 'COMPRA DE DÍVIDA';

/**
 * "Categoria do produto" (`categoryNameOptions`, creditProductReadModel.ts:331-…): o valor técnico é o
 * nome que vai no fio (`productCategoryName`), em maiúsculas como o legado.
 */
export const PRODUCT_CATEGORY_NAME = defineEnum({
  CCB: { label: msg`CCB` },
  'CCB CLEAN': { label: msg`CCB CLEAN` },
  'CCB FINANCIAMENTO VE': { label: msg`CCB FINANCIAMENTO VE` },
  'CCB PRICE': { label: msg`CCB PRICE` },
  CDC: { label: msg`CDC` },
  'CDC FGTS': { label: msg`CDC FGTS` },
  'COMPRA DE DÍVIDA': { label: msg`COMPRA DE DÍVIDA` },
  'CONSIGNADO PRIVADO': { label: msg`CONSIGNADO PRIVADO` },
  'CONSIGNADO PRIVADO CDC': { label: msg`CONSIGNADO PRIVADO CDC` },
  'CONSIGNADO PRIVADO LEILÃO': { label: msg`CONSIGNADO PRIVADO LEILÃO` },
  'CONSIGNADO PÚBLICO': { label: msg`CONSIGNADO PÚBLICO` },
  'CAR EQUITY': { label: msg`CAR EQUITY` },
  'FINANCIAMENTO DE VEICULO': { label: msg`FINANCIAMENTO DE VEÍCULO` },
  'CARTÃO BENEFÍCIO': { label: msg`CARTÃO BENEFÍCIO` },
});

/** Stepper do cabeçalho (`BatchAssignmentForm.tsx:76-81`, `timelineType='batchAssignment'`). */
/* eslint-disable lingui/no-unlocalized-strings -- ids e status técnicos do fio */
export const STEPPER_STEPS = [
  { id: 'draft', label: msg`Rascunho`, statuses: ['Open'] },
  { id: 'processing', label: msg`Processamento`, statuses: ['Processing', 'Calculated'] },
  { id: 'signature', label: msg`Assinatura`, statuses: ['Signatures'] },
  { id: 'liquidation', label: msg`Liquidação`, statuses: ['Liquidation'] },
  { id: 'finished', label: msg`Encerrado`, statuses: ['Finished'] },
] as const;
/* eslint-enable lingui/no-unlocalized-strings */
